import { ng } from "@common/ngds";
import { selfWeightLineLoads, storedSelfWeight } from "@common/helpers/elementMass";
import type { ElementDbs, SelfWeightPiece } from "@common/helpers/elementMass";
import { ProjectSlice } from "../src/features/projects/projectSlice";
import { PRUNE_EXCLUDED_ACTION_TYPES } from "./syncMiddleware";

// Keeps the self-weight line loads in step with the column they are the weight of.
//
// The printed weight is derived from these loads (sx-center print integrates them), so a stale
// self-weight is not just a wrong analysis — it is a printout whose weight disagrees with the column
// drawn next to it. Refreshing used to be a call sprinkled through the components that edit
// geometry, and the ones that forgot (the column length) left it stale.
//
// So it is decided here, once, after every ProjectSlice action that invalidates output: anything that
// can make the old results wrong can have changed the weight. It is recomputed and written back only
// when the numbers differ, so moving a bar or editing a load costs one comparison and no dispatch.
// Not debounced on purpose: a window in which the loads lag the geometry is a window in which an
// analysis or a save can bake the wrong self-weight in.

const SYNC_ACTION = ProjectSlice.actions.syncSelfWeightLoads.type;

const samePieces = (a: SelfWeightPiece[], b: SelfWeightPiece[]): boolean => {
  const close = (x: number, y: number) => Math.abs(x - y) <= 1e-9 * Math.max(1, Math.abs(x), Math.abs(y));
  return (
    a.length === b.length &&
    a.every(
      (p, i) =>
        close(p.start, b[i].start) &&
        close(p.end, b[i].end) &&
        close(p.qStart, b[i].qStart) &&
        close(p.qEnd, b[i].qEnd),
    )
  );
};

const createSelfWeightMiddleware = () => {
  // What the last recomputation was based on. Immer keeps untouched subtrees by reference, so an
  // edit that leaves the column, its materials and its sections alone (a load, a combination, a
  // setting) is recognised without computing anything.
  let basis: unknown[] = [];

  return (store: any) => (next: any) => (action: any) => {
    const result = next(action);

    const type = action.type;
    if (typeof type !== "string" || !type.startsWith("ProjectSlice/")) return result;
    if (type === SYNC_ACTION || PRUNE_EXCLUDED_ACTION_TYPES.has(type)) return result;

    const input = store.getState().ProjectSlice.project?.data?.data?.input;
    const structure = input?.structures?.[0];
    const beamJson = structure?.elements?.[0]?.beam;
    if (!beamJson) return result;

    const nextBasis = [beamJson, input.mtrlDb, input.secDb, structure.loading?.loadcases];
    if (nextBasis.every((part, i) => part === basis[i])) return result;

    // A column pointing at a material or section not yet in the dbs is mid-edit, not weightless:
    // leave the loads alone until the reference resolves.
    const inDb = (db: any[] | undefined, guid: string | undefined) => !!guid && !!db?.some((x) => x?.id?.guid === guid);
    if (!inDb(input.mtrlDb, beamJson.mtrlGuid)) return result;
    if (!(beamJson.segments ?? []).every((seg: any) => inDb(input.secDb, seg?.layer?.start?.secGuid))) return result;

    basis = nextBasis;

    const stored = storedSelfWeight(ng.loading.Data.fromObject(structure.loading ?? {}));
    if (stored === null) return result; // self-weight switched off

    const pieces = selfWeightLineLoads(input as ElementDbs, ng.beam.Data.fromObject(beamJson));
    if (!samePieces(pieces, stored)) {
      store.dispatch(ProjectSlice.actions.syncSelfWeightLoads({ pieces }));
    }
    return result;
  };
};

export default createSelfWeightMiddleware;
